import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";

const SIDEBAR_STYLES = `
  @property --side-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }

  @keyframes side-spin-light {
    to { --side-angle: 360deg; }
  }

  .sb-btn-outer {
    position: relative;
    padding: 2px;
    border-radius: 16px;
    background: transparent;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-bottom: 12px;
    border: 1px solid #e0f2fe;
    background: #fff;
    box-shadow: 0 4px 12px rgba(186, 230, 253, 0.15);
  }

  .sb-btn-ring {
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 2px;
    background: conic-gradient(
      from var(--side-angle, 0deg),
      transparent 0deg,
      #0284c7 60deg,
      #7c3aed 120deg,
      transparent 180deg
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  .sb-btn-outer:hover .sb-btn-ring {
    opacity: 1;
    animation: side-spin-light 1.8s linear infinite;
  }

  .sb-btn-outer:hover {
    background: #fff;
    transform: translateX(8px);
    box-shadow: 0 12px 24px rgba(186, 230, 253, 0.4);
  }

  .sb-btn-active {
    background: #f0f9ff !important;
    border-color: #0284c7 !important;
    box-shadow: 0 8px 20px rgba(186, 230, 253, 0.5) !important;
  }
  
  .sb-btn-inner {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 18px;
    border-radius: 15px;
    font-size: 15px;
    font-weight: 600;
    color: #475569;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;
  }

  .sb-btn-outer:hover .sb-btn-inner { color: #0369a1; }
  .sb-btn-active .sb-btn-inner { color: #0284c7; }

  /* ── Profile card at bottom ── */
  .sb-profile-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 16px;
    background: #fff;
    border: 1px solid #e0f2fe;
    box-shadow: 0 4px 12px rgba(186, 230, 253, 0.2);
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    user-select: none;
  }

  .sb-profile-card:hover {
    background: #f0f9ff;
    border-color: #0284c7;
    box-shadow: 0 8px 20px rgba(186, 230, 253, 0.45);
    transform: translateY(-1px);
  }

  .sb-profile-avatar {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, #0284c7 0%, #7c3aed 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    box-shadow: 0 4px 10px rgba(2, 132, 199, 0.3);
    position: relative;
  }

  .sb-verified-badge {
    position: absolute;
    bottom: -5px;
    right: -5px;
    width: 18px;
    height: 18px;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    border-radius: 50%;
    border: 2px solid #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    color: #fff;
    font-weight: 800;
    box-shadow: 0 2px 6px rgba(34, 197, 94, 0.5);
    animation: sb-badge-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  @keyframes sb-badge-pop {
    from { transform: scale(0); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }

  .sb-profile-info { flex: 1; min-width: 0; }

  .sb-profile-name {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sb-profile-role {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
    margin-top: 1px;
  }

  /* Popup menu above profile card */
  .sb-profile-popup {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #e0f2fe;
    border-radius: 16px;
    box-shadow: 0 -8px 32px rgba(186, 230, 253, 0.35), 0 4px 16px rgba(0,0,0,0.08);
    overflow: hidden;
    animation: sb-popup-in 0.22s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    z-index: 10;
  }

  @keyframes sb-popup-in {
    from { opacity: 0; transform: translateY(10px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .sb-popup-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 13px 16px;
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 0.18s, color 0.18s;
    text-align: left;
  }

  .sb-popup-item:hover {
    background: #f0f9ff;
    color: #0284c7;
  }

  .sb-popup-divider {
    height: 1px;
    background: #f1f5f9;
    margin: 0 12px;
  }

  .sb-popup-logout {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 13px 16px;
    font-size: 14px;
    font-weight: 700;
    color: #dc2626;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 0.18s;
    text-align: left;
  }

  .sb-popup-logout:hover { background: #fef2f2; }

  /* Chevron */
  .sb-chevron {
    font-size: 10px;
    color: #94a3b8;
    transition: transform 0.25s ease, color 0.2s;
    flex-shrink: 0;
  }
  .sb-chevron.open {
    transform: rotate(180deg);
    color: #0284c7;
  }

  /* ── Mobile sidebar slide ── */
  .user-sidebar {
    width: 256px;
    height: 100vh;
    overflow-y: auto;
    background: #f8fafc;
    color: #1e293b;
    display: flex;
    flex-direction: column;
    padding: 24px;
    border-right: 1px solid #e0f2fe;
    box-shadow: 4px 0 24px rgba(186,230,253,0.2);
    position: fixed;
    top: 0;
    left: 0;
    z-index: 300;
    transition: transform 0.32s cubic-bezier(0.4,0,0.2,1);
  }

  .user-sidebar-overlay {
    display: none;
    position: fixed;
    inset: 0;
    z-index: 250;
    background: rgba(15,23,42,0.45);
    backdrop-filter: blur(2px);
    animation: sb-fadeIn 0.2s ease;
  }
  @keyframes sb-fadeIn { from { opacity:0; } to { opacity:1; } }

  @media (max-width: 768px) {
    .user-sidebar { transform: translateX(-100%); }
    .user-sidebar.open { transform: translateX(0); }
    .user-sidebar-overlay { display: block; }
  }
`;

const getInitials = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");

const Sidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isVerified, setIsVerified] = useState(false);
    const styleInjected = useRef(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const profileRef = useRef(null);

    useEffect(() => {
        if (!styleInjected.current) {
            const tag = document.createElement("style");
            tag.textContent = SIDEBAR_STYLES;
            document.head.appendChild(tag);
            styleInjected.current = true;
        }
    }, []);

    let user = null;
    try {
        const raw = localStorage.getItem("user");
        user = raw && raw !== "undefined" ? JSON.parse(raw) : null;
    } catch {
        user = null;
    }
    const displayName = user?.name || user?.username || "User";
    const initials = getInitials(displayName);
    const verified = isVerified || user?.is_verified;
    const userId = user?.id || user?.userId;

    useEffect(() => {
        import("axios").then((axios) => {
            if (userId) {
                axios.default
                    .get(`${API}/api/users/profile/${userId}`)
                    .then((res) => setIsVerified(res.data.is_verified))
                    .catch((err) => console.error("Error fetching verification status", err));
            }
        });
    }, [userId]);

    /* Close popup when clicking outside */
    useEffect(() => {
        if (!showPopup) return;
        const handler = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target))
                setShowPopup(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showPopup]);

    const menu = [
        { name: "Dashboard", path: "/dashboard", icon: "🏠" },
        { name: "Doctor List", path: "/doctors", icon: "🏥" },
        { name: "Appointments", path: "/appointments", icon: "📅" },
        { name: "Screening Portal", path: "/Screening_test/patient", icon: "🩺" },
        { name: "Notifications", path: "/notifications", icon: "🔔" },
        { name: "Document Upload", path: "/upload", icon: "📂" },
        { name: "Previous Records", path: "/records", icon: "📊" },
        { name: "My Profile", path: "/profile", icon: "👤" },
    ];

    const handleNav = (item) => {
  if (typeof item === 'string') {
    navigate(item, { replace: item !== "/dashboard" });
    setShowPopup(false);
    if (onClose) onClose();
    return;
  }

  if (item.external) {
    window.location.href = item.url;
    return;
  }

  if (item.path && item.path.includes("/Screening_test")) {
    window.location.href = item.path;
    return;
  }

  navigate(item.path, {
    replace: item.path !== "/dashboard",
  });

  setShowPopup(false);

  if (onClose) onClose();
};

    const confirmLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
    };

    return (
        <>
            {isOpen && <div className="user-sidebar-overlay" onClick={onClose} />}

            <div className={`user-sidebar ${isOpen ? "open" : ""}`}>
                {/* Logo */}
                <div className="flex items-center gap-3 mb-10 cursor-pointer group" onClick={() => handleNav("/dashboard")}>
                    <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center text-2xl shadow-lg shadow-blue-200 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                        💉
                    </div>
                    <div>
                        <h2 className="text-xl font-extrabold tracking-tight text-slate-800 leading-tight">
                            Health <span className="text-blue-500">Panel</span>
                        </h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Medical Dashboard</p>
                    </div>
                </div>

                <div className="flex flex-col flex-1">
                    {menu.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <div
                                key={item.path || item.url || item.name}
                                className={`sb-btn-outer ${isActive ? "sb-btn-active" : ""}`}
                                onClick={() => handleNav(item)}
                            >
                            <div className="sb-btn-ring" />
                                <button className="sb-btn-inner flex items-center justify-between w-full">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">{item.icon}</span>
                                        <span>{item.name}</span>
                                    </div>
                                    {item.name === "My Profile" && verified && (
                                        <span className="text-emerald-500 bg-emerald-50 p-1 rounded-full border border-emerald-100 shadow-sm" title="Verified Patient">✨</span>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* ── Profile card ── */}
                <div className="mt-auto pt-6 border-t border-[#e0f2fe] sticky bottom-0 bg-[#f8fafc]">
                    <div className="sb-profile-card" ref={profileRef} onClick={() => setShowPopup((v) => !v)}>

                        {/* Popup with My Profile + Logout */}
                        {showPopup && (
                            <div className="sb-profile-popup">
                                <button
                                    className="sb-popup-item"
                                    onClick={(e) => { e.stopPropagation(); handleNav("/profile"); }}
                                >
                                    <span>👤</span>
                                    <span>My Profile</span>
                                    {verified && (
                                        <span style={{
                                            marginLeft: "auto",
                                            fontSize: "10px",
                                            fontWeight: 700,
                                            color: "#16a34a",
                                            background: "#dcfce7",
                                            border: "1px solid #bbf7d0",
                                            borderRadius: "999px",
                                            padding: "2px 8px",
                                            whiteSpace: "nowrap",
                                        }}>
                                            ✓ Verified
                                        </span>
                                    )}
                                </button>

                                <div className="sb-popup-divider" />

                                <button
                                    className="sb-popup-logout"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowPopup(false);
                                        setShowLogoutModal(true);
                                    }}
                                >
                                    <span>🚪</span>
                                    <span>Logout</span>
                                </button>
                            </div>
                        )}

                        {/* Avatar */}
                        <div className="sb-profile-avatar">
                            {initials || "U"}
                            {verified && (
                                <span className="sb-verified-badge" title="Verified Patient">✓</span>
                            )}
                        </div>

                        {/* Name + role */}
                        <div className="sb-profile-info">
                            <div className="sb-profile-name">{displayName}</div>
                            <div className="sb-profile-role">
                                {verified ? "✨ Verified Patient" : "Patient"}
                            </div>
                        </div>

                        {/* Chevron */}
                        <span className={`sb-chevron ${showPopup ? "open" : ""}`}>▲</span>
                    </div>
                </div>
            </div>

            {/* Logout Confirm Modal */}
            {showLogoutModal &&
                createPortal(
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl p-6 w-[320px] shadow-2xl">
                            <h2 className="text-lg font-bold text-slate-800 mb-2">🚪 Confirm Logout</h2>
                            <p className="text-sm text-slate-500 mb-6">Are you sure you want to logout?</p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowLogoutModal(false)}
                                    className="flex-1 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmLogout}
                                    className="flex-1 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition"
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default Sidebar;